import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChartBar, FilePdf, Flame, Warning } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CATEGORY_COLORS } from "@/lib/meta";
import { exportReportPDF } from "@/lib/pdf";

const PROPS = [
  ["hardness", "Microhardness"],
  ["porosity", "Porosity"],
  ["bond_strength", "Bond Strength"],
  ["density", "Density"],
  ["melting_point", "Melting Point"],
];

export default function MaterialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/materials/${id}`)
      .then((r) => setM(r.data))
      .catch(() => setError("Material not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const exportPdf = () => {
    if (!m) return;
    const rows = PROPS.filter(([k]) => m[k]).map(([k, label]) => `<tr><th>${label}</th><td>${m[k]}</td></tr>`).join("");
    const body = `
      <h2>Overview</h2>
      <table>
        <tr><th>Name</th><td>${m.name}</td></tr>
        <tr><th>Category</th><td>${m.category}</td></tr>
        ${m.composition ? `<tr><th>Composition</th><td>${m.composition}</td></tr>` : ""}
        <tr><th>Recommended Processes</th><td>${(m.recommended_processes || []).join(", ")}</td></tr>
      </table>
      <h2>Coating Properties</h2>
      <table>${rows || "<tr><td>No property data</td></tr>"}</table>
      ${m.spray_parameters ? `<h2>Spray Parameters</h2><p>${m.spray_parameters}</p>` : ""}
      ${m.microstructure ? `<h2>Coating Microstructure</h2><p>${m.microstructure}</p>` : ""}
      ${m.applications ? `<h2>Applications</h2><p>${m.applications}</p>` : ""}
      ${m.notes ? `<h2>Notes</h2><p>${m.notes}</p>` : ""}
    `;
    exportReportPDF({ title: m.name, subtitle: "Material Datasheet", bodyHTML: body });
  };

  if (loading) {
    return <div className="grain min-h-[50vh] flex items-center justify-center font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>;
  }
  if (error || !m) {
    return (
      <div className="grain mx-auto max-w-2xl px-4 py-20 text-center">
        <Warning size={40} className="text-destructive mx-auto mb-3" />
        <h1 className="font-heading font-black text-white text-2xl">{error || "Not found"}</h1>
        <Button onClick={() => navigate("/database")} className="mt-5 rounded-sm bg-primary text-white font-mono uppercase text-xs tracking-widest">Back to Database</Button>
      </div>
    );
  }

  return (
    <div className="grain mx-auto max-w-[1100px] px-4 sm:px-6 py-8">
      <Link to="/database" data-testid="back-to-db" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors mb-6">
        <ArrowLeft size={14} weight="bold" /> Database
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading font-black text-white text-3xl">{m.name}</h1>
            <span className={`border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide rounded-sm ${CATEGORY_COLORS[m.category] || CATEGORY_COLORS.Other}`}>{m.category}</span>
          </div>
          {m.composition && <div className="font-mono text-sm text-secondary mt-2">{m.composition}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button asChild variant="outline" className="rounded-sm border-border bg-card hover:bg-muted text-muted-foreground hover:text-white font-mono uppercase text-xs tracking-widest">
            <Link to={`/compare?material=${encodeURIComponent(m.name)}`} data-testid="detail-compare-btn">
              <ChartBar size={16} weight="bold" className="mr-1.5" /> Compare
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm border-border bg-card hover:bg-muted text-muted-foreground hover:text-white font-mono uppercase text-xs tracking-widest">
            <Link to={`/?q=${encodeURIComponent(m.name)}&run=1`} data-testid="detail-advisor-btn">
              <Flame size={16} weight="bold" className="mr-1.5" /> Advisor
            </Link>
          </Button>
          <Button data-testid="detail-pdf-btn" onClick={exportPdf} className="rounded-sm bg-primary hover:bg-primary/90 text-white font-mono uppercase text-xs tracking-widest">
            <FilePdf size={16} weight="bold" className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {m.micrograph_path && (
          <div className="border border-border bg-card p-3 lg:col-span-3">
            <h2 className="font-heading font-bold text-white text-sm mb-2 px-2">SEM / Cross-section Micrograph</h2>
            <img
              data-testid="detail-micrograph"
              src={`${API}/materials/${m.id}/image`}
              alt={`${m.name} micrograph`}
              className="w-full max-h-[420px] object-contain rounded-sm bg-black/40"
            />
          </div>
        )}
        {/* Processes */}
        <div className="border border-border bg-card p-5">
          <h2 className="font-heading font-bold text-white text-sm mb-3">Recommended Processes</h2>
          <div className="flex flex-wrap gap-2">
            {(m.recommended_processes || []).map((p) => (
              <span key={p} className="border border-primary/40 bg-primary/10 text-primary px-2.5 py-1 text-xs font-mono rounded-sm">{p}</span>
            ))}
            {(!m.recommended_processes || m.recommended_processes.length === 0) && (
              <span className="text-sm text-muted-foreground">Not specified</span>
            )}
          </div>
        </div>

        {/* Properties */}
        <div className="border border-border bg-card p-5 lg:col-span-2">
          <h2 className="font-heading font-bold text-white text-sm mb-3">Coating Properties</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            {PROPS.map(([k, label]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="font-mono text-sm text-white mt-0.5">{m[k] || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {m.spray_parameters && (
          <div className="border border-border bg-card p-5 lg:col-span-3">
            <h2 className="font-heading font-bold text-white text-sm mb-2">Spray Parameters</h2>
            <p className="font-mono text-sm text-muted-foreground leading-relaxed">{m.spray_parameters}</p>
          </div>
        )}
        {m.microstructure && (
          <div className="border border-border bg-card p-5 lg:col-span-3">
            <h2 className="font-heading font-bold text-white text-sm mb-2">Coating Microstructure</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{m.microstructure}</p>
          </div>
        )}
        {m.applications && (
          <div className="border border-border bg-card p-5 lg:col-span-2">
            <h2 className="font-heading font-bold text-white text-sm mb-2">Applications</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{m.applications}</p>
          </div>
        )}
        {m.notes && (
          <div className="border border-border bg-card p-5">
            <h2 className="font-heading font-bold text-white text-sm mb-2">Notes</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{m.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
